import { DatePipe, JsonPipe, NgForOf, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { RegistrationService } from '@core/http/registration.service';
import {
  SolicitudRegistro,
  SolicitudRegistroPayload,
} from '@core/models/Registration';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import Swal from 'sweetalert2';
import {
  RegistrationRequestDetailDialogComponent,
} from './registration-request-detail-dialog.component';

@Component({
  selector: 'app-registration-request-list',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    JsonPipe,
    DatePipe,
    ReactiveFormsModule,
    BreadcrumbComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule,
  ],
  templateUrl: './registration-request-list.component.html',
  styleUrl: './registration-request-list.component.scss',
})
export class RegistrationRequestListComponent implements OnInit {
  breadcrumbItems = [
    { label: 'MEDICOS', link: '/doctor/doctor-list' },
    { label: 'SOLICITUDES DE REGISTRO' },
  ];

  filterForm: UntypedFormGroup;
  displayedColumns = ['folio', 'tipo', 'estatus', 'nombre', 'email', 'fecha', 'acciones'];

  requests: SolicitudRegistro[] = [];
  loading = false;
  approvingId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private registrationService: RegistrationService,
    private httpMsgHandler: HttpMesageHandlerService,
  ) {
    this.filterForm = this.fb.group({
      estatus: ['PENDIENTE'],
      tipoSolicitud: ['MEDICO'],
      folio: [''],
      email: [''],
    });
  }

  ngOnInit(): void {
    this.loadRequests();
  }

  loadRequests(): void {
    this.loading = true;
    this.registrationService.getRequests(this.filterForm.getRawValue()).subscribe({
      next: (response) => {
        this.loading = false;
        this.requests = [...(response.data ?? [])];

        if (!this.requests.length) {
          return;
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openRequestDetail(request: SolicitudRegistro): void {
    this.dialog.open(RegistrationRequestDetailDialogComponent, {
      width: '960px',
      maxWidth: '96vw',
      panelClass: 'rmd-dialog-premium',
      data: {
        request,
        payload: this.parsePayload(request.payloadJson),
      },
    });
  }

  approve(request: SolicitudRegistro): void {
    if (request.estatus !== 'PENDIENTE') {
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'APROBAR SOLICITUD',
      text: `SE APROBARA LA SOLICITUD ${request.folio}.`,
      showCancelButton: true,
      confirmButtonText: 'APROBAR',
      cancelButtonText: 'CANCELAR',
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.approvingId = request.idSolicitudRegistro;
      this.registrationService.approveDoctorRequest(request.idSolicitudRegistro).subscribe({
        next: (response) => {
          this.approvingId = null;
          this.httpMsgHandler.handleMsg(response as any);
          this.loadRequests();
        },
        error: () => {
          this.approvingId = null;
        },
      });
    });
  }

  reject(request: SolicitudRegistro): void {
    if (request.estatus !== 'PENDIENTE') {
      return;
    }

    Swal.fire({
      icon: 'warning',
      title: 'RECHAZAR SOLICITUD',
      text: `CAPTURA EL MOTIVO DE RECHAZO PARA ${request.folio}.`,
      input: 'textarea',
      inputPlaceholder: 'ESCRIBE EL MOTIVO DE RECHAZO',
      inputAttributes: {
        autocapitalize: 'on',
      },
      showCancelButton: true,
      confirmButtonText: 'RECHAZAR',
      cancelButtonText: 'CANCELAR',
      inputValidator: (value) => {
        if (!String(value ?? '').trim()) {
          return 'EL MOTIVO DE RECHAZO ES OBLIGATORIO.';
        }
        return null;
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      const motivoRechazo = String(result.value ?? '').trim();
      if (!motivoRechazo) return;

      this.approvingId = request.idSolicitudRegistro;
      this.registrationService.rejectRequest(request.idSolicitudRegistro, motivoRechazo).subscribe({
        next: (response) => {
          this.approvingId = null;
          this.httpMsgHandler.handleMsg(response as any);
          this.loadRequests();
        },
        error: () => {
          this.approvingId = null;
        },
      });
    });
  }

  clearFilters(): void {
    this.filterForm.reset({
      estatus: 'PENDIENTE',
      tipoSolicitud: 'MEDICO',
      folio: '',
      email: '',
    });
    this.loadRequests();
  }

  private parsePayload(payloadJson?: string | null): SolicitudRegistroPayload | null {
    if (!payloadJson) return null;

    try {
      return JSON.parse(payloadJson) as SolicitudRegistroPayload;
    } catch {
      return null;
    }
  }
}
